import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { getAge } from "@/utils/dateUtil";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { ResumeCitizenProceduresIndicatorB3Data } from "@/services/esus/indicator/esb/b3/getResumeCitizensProceduresIndicatorB3";
import {
	DetailedCitizenProceduresIndicatorB3,
	getDetailedCitizenProcedureIndicatorB3,
} from "@/services/esus/indicator/esb/b3/getDetailedCitizenProceduresIndicatorB3";

import { IndicatorEsbB3CitizensFilters } from "./IndicatorEsbB3CitizensFilters";
import { IndicatorEsbB3CitizensTable } from "./IndicatorEsbB3CitizensTable";
import { IndicatorEsbB3CitizenAttendancesHistory } from "./IndicatorEsbB3CitizenHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB3CitizensProps = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizens: Paginated<ResumeCitizenProceduresIndicatorB3Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeCitizensProceduresIndicatorB3: () => Promise<
		ResumeCitizenProceduresIndicatorB3Data[]
	>;
};

export function IndicatorEsbB3Citizens({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizens,
	pageNumber,
	setPageNumber,
	handleGetAllResumeCitizensProceduresIndicatorB3,
}: Readonly<IndicatorEsbB3CitizensProps>) {
	const { uf, ibgeCode } = useCurrentAccount();
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithProcedures, setCitizenWithProcedures] =
		useState<DetailedCitizenProceduresIndicatorB3>(new DetailedCitizenProceduresIndicatorB3());
	const [isLoadingCitizenWithProcedures, setIsLoadingCitizenWithProcedures] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithProcedures(citizenId: number, ine?: string) {
		setIsLoadingCitizenWithProcedures(true);
		setCitizenWithProcedures(
			await getDetailedCitizenProcedureIndicatorB3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne || ine,
				citizenId,
			})
		);
		setIsLoadingCitizenWithProcedures(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllResumeCitizensProceduresIndicatorB3();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorB3ProceduresToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Cidadãos acompanhados - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB3ProceduresToGenericObjectArray(
		citizensWithProcedures: ResumeCitizenProceduresIndicatorB3Data[]
	) {
		return citizensWithProcedures.map(
			(citizenWithAttendances) =>
				({
					Nome: citizenWithAttendances.fullName,
					DN: citizenWithAttendances.birthdate
						? dayjs(citizenWithAttendances.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithAttendances.birthdate
						? getAge(citizenWithAttendances.birthdate)
						: "",
					Documento: citizenWithAttendances.cpf ?? citizenWithAttendances.cns ?? "",
					Area: citizenWithAttendances.area ?? "FA",
					"Equipe ESF Cidadão": citizenWithAttendances.healthTeamName ?? "",
					"Equipe ESF Ine Cidadão": citizenWithAttendances.healthTeamIne ?? "",
					"Equipe ESB Atendimento": citizenWithAttendances.esbHealthTeamName ?? "",
					"Equipe ESB Ine Atendimento": citizenWithAttendances.esbHealthTeamIne ?? "",
					"Equipe ESF (vínculada a ESB atendimento)":
						citizenWithAttendances.linkedHealthTeamName ?? "",
					"Equipe ESF Ine (vínculada a ESB atendimento)":
						citizenWithAttendances.linkedHealthTeamIne ?? "",
					Exodontias: citizenWithAttendances.totalToothExtractionProcedures,
					Procedimentos: citizenWithAttendances.totalProcedures,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithProcedures || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsbB3CitizensFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB3CitizensTable
							citizensWithProcedures={paginatedCitizens.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithProcedures}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizens?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizens.pageSize}
								totalPages={paginatedCitizens.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Procedimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsbB3CitizenAttendancesHistory
							citizenWithDentalAttendanceProcedures={citizenWithProcedures}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
